import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { Shell } from "@/components/Shell";
import { BottomSheet } from "@/components/BottomSheet";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { ClientForm } from "@/pages/JobFiles";
import { DocRow } from "@/pages/Home";
import { money, fmtDate, DOC_META } from "@/lib/format";
import { Phone, Mail, MapPin, Plus, Pencil, Trash2, FileText, Receipt, Recycle, FolderInput, Check } from "lucide-react";

const PickRow = ({ d, selected, onToggle }) => {
  const m = DOC_META[d.type] || {};
  return (
    <button data-testid={`pick-doc-${d.id}`} onClick={onToggle} className={`w-full rounded-[12px] border p-3 flex items-center gap-3 text-left transition-colors ${selected ? "border-brandPrimary bg-brandTertiary/40" : "border-border bg-surface"}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${selected ? "bg-brandPrimary" : "border-2 border-borderStrong"}`}>{selected && <Check size={14} className="text-onBrand" />}</div>
      <div className="flex-1 min-w-0">
        <p className="font-heading font-bold text-sm text-onSurface truncate">{d.number} · {d.data?.title || m.label}</p>
        <p className="text-xs text-muted-foreground truncate">{d.client?.name || "No client"} · {fmtDate(d.created_at)}</p>
      </div>
      {d.data?.total != null && d.type !== "waste_note" && <span className="text-sm font-heading font-bold text-onSurface">{money(d.data.total)}</span>}
    </button>
  );
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editSheet, setEditSheet] = useState(false);
  const [addSheet, setAddSheet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [picking, setPicking] = useState(null); // { docs, selected:Set }
  const [addBusy, setAddBusy] = useState(false);

  const load = () => api.get(`/clients/${id}`).then(({ data }) => setData(data)).catch(() => navigate("/folders")).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const save = async (form) => {
    setSaving(true);
    try { await api.patch(`/clients/${id}`, form); toast.success("Updated"); setEditSheet(false); load(); }
    catch { toast.error("Update failed"); } finally { setSaving(false); }
  };

  const del = async () => {
    setDelBusy(true);
    try { await api.delete(`/clients/${id}`); toast.success("Folder deleted · documents kept"); navigate("/folders"); }
    catch { toast.error("Couldn't delete"); setDelBusy(false); }
  };

  const openPicker = async () => {
    try {
      const { data: docData } = await api.get("/documents");
      const available = docData.documents.filter((d) => d.client_id !== id);
      setPicking({ docs: available, selected: new Set() });
    } catch { toast.error("Couldn't load documents"); }
  };

  const toggle = (docId) => setPicking((p) => { const s = new Set(p.selected); s.has(docId) ? s.delete(docId) : s.add(docId); return { ...p, selected: s }; });

  const addSelected = async () => {
    const ids = [...picking.selected];
    if (!ids.length) { setPicking(null); return; }
    setAddBusy(true);
    try {
      await Promise.all(ids.map((docId) => api.patch(`/documents/${docId}`, { client_id: id })));
      toast.success(`Added ${ids.length} document${ids.length === 1 ? "" : "s"}`);
      setPicking(null); setLoading(true); load();
    } catch { toast.error("Couldn't add documents"); } finally { setAddBusy(false); }
  };

  if (loading || !data) return <Shell back title="Loading…"><div className="h-40 bg-surfaceTertiary rounded-[12px] animate-pulse" /></Shell>;
  const c = data.client;

  const newDoc = (type) => { setAddSheet(false); navigate(`/new/${type}?client=${id}`); };

  return (
    <Shell back title={c.name} right={
      <button data-testid="edit-client-btn" onClick={() => setEditSheet(true)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-surfaceTertiary"><Pencil size={18} /></button>
    }>
      <div className="bg-onSurface text-onBrand rounded-[20px] p-5 animate-fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-white/60">Total invoiced</p>
        <p className="font-heading text-3xl font-extrabold mt-1">{money(data.total_invoiced)}</p>
        <p className="text-xs text-white/60 mt-1">{data.documents.length} document{data.documents.length === 1 ? "" : "s"} on file</p>
      </div>

      <div className="mt-3 bg-surface rounded-[16px] border border-border p-4 space-y-2">
        {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-3 text-sm text-onSurface"><Phone size={16} className="text-brandPrimary" /> {c.phone}</a>}
        {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-3 text-sm text-onSurface"><Mail size={16} className="text-brandPrimary" /> {c.email}</a>}
        {c.address && <div className="flex items-center gap-3 text-sm text-onSurface"><MapPin size={16} className="text-brandPrimary" /> {c.address}</div>}
        {c.notes && <p className="text-sm text-muted-foreground pt-1 border-t border-border mt-2">{c.notes}</p>}
        {!c.phone && !c.email && !c.address && <p className="text-sm text-muted-foreground">No contact details.</p>}
      </div>

      <div className="flex gap-2 mt-3">
        <button data-testid="add-doc-to-client" onClick={() => setAddSheet(true)} className="flex-1 bg-brandPrimary text-onBrand rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <Plus size={20} /> New document
        </button>
        <button data-testid="view-add-docs-btn" onClick={openPicker} className="flex-1 bg-brandTertiary text-brandSecondary rounded-full h-12 font-heading font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <FolderInput size={18} /> Add existing
        </button>
      </div>

      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-6 mb-3">History</p>
      {data.documents.length === 0 ? (
        <div className="bg-surface rounded-[16px] border border-dashed border-borderStrong p-8 text-center">
          <p className="text-sm text-muted-foreground mb-4">This folder is empty. Add a new document or pull in existing ones.</p>
          <button data-testid="empty-add-existing" onClick={openPicker} className="bg-brandTertiary text-brandSecondary rounded-full px-5 h-11 font-heading font-bold text-sm active:scale-95 transition-transform inline-flex items-center gap-2"><FolderInput size={16} /> View &amp; add documents</button>
        </div>
      ) : (
        <div className="space-y-2">{data.documents.map((d) => <DocRow key={d.id} d={d} onClick={() => navigate(`/doc/${d.id}`)} />)}</div>
      )}

      <button data-testid="delete-client-btn" onClick={() => setConfirmDel(true)} className="w-full mt-6 mb-2 text-error text-sm font-semibold flex items-center justify-center gap-2 py-3"><Trash2 size={16} /> Delete folder</button>

      <BottomSheet open={editSheet} onClose={() => setEditSheet(false)} title="Edit client">
        <ClientForm initial={c} onSubmit={save} submitting={saving} cta="Save changes" />
      </BottomSheet>

      <BottomSheet open={addSheet} onClose={() => setAddSheet(false)} title="New document">
        <div className="grid grid-cols-3 gap-3">
          {[["quote", FileText, "#E3592A"], ["invoice", Receipt, "#166534"], ["waste_note", Recycle, "#CA8A04"]].map(([type, Icon, color]) => (
            <button key={type} data-testid={`client-new-${type}`} onClick={() => newDoc(type)} className="flex flex-col items-center gap-2 p-4 rounded-[12px] border border-border hover:border-brandPrimary transition-colors active:scale-95">
              <Icon size={24} style={{ color }} />
              <span className="font-heading font-bold text-xs capitalize">{type.replace("_note", "")}</span>
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* Add existing documents picker */}
      <BottomSheet open={!!picking} onClose={() => setPicking(null)} title={`Add to ${c.name}`} testid="add-existing-sheet">
        {picking && (
          <>
            <p className="text-sm text-muted-foreground mb-3">Tap any documents to move them into this folder.</p>
            {picking.docs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No other documents to add.</p>
            ) : (
              <div className="space-y-2 max-h-[46vh] overflow-y-auto no-scrollbar">
                {picking.docs.map((d) => <PickRow key={d.id} d={d} selected={picking.selected.has(d.id)} onToggle={() => toggle(d.id)} />)}
              </div>
            )}
            <button data-testid="add-existing-confirm" disabled={addBusy} onClick={addSelected} className="w-full mt-4 bg-brandPrimary text-onBrand rounded-full h-12 font-heading font-bold active:scale-[0.98] transition-transform disabled:opacity-50">
              {addBusy ? "Adding…" : picking.selected.size ? `Add ${picking.selected.size} document${picking.selected.size === 1 ? "" : "s"}` : "Done"}
            </button>
          </>
        )}
      </BottomSheet>

      <ConfirmSheet
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={del}
        busy={delBusy}
        title="Delete this folder?"
        message={`"${c.name}" will be removed. Don't worry — the ${data.documents.length} document${data.documents.length === 1 ? "" : "s"} inside ${data.documents.length === 1 ? "stays" : "stay"} safe and just ${data.documents.length === 1 ? "becomes" : "become"} unfiled.`}
        confirmLabel="Delete folder"
        testid="folder-delete-confirm"
      />
    </Shell>
  );
}
